<script setup lang="ts">
import { onBeforeMount, toRefs, ref, computed } from 'vue';
import useVuelidate from '@vuelidate/core';
import { required, minLength, email, requiredIf } from '@vuelidate/validators';
import { EMIT, PERMISSIONS } from '@/enum';
import { GeneralProfileInterface } from '@/types';
import { usePeopleStore, useTimezoneStore, useAuthStore, PersonStores, usePeopleTypesStore } from '@/store';
import { can, usePersonTypes } from '@/composables';

const peopleStore = usePeopleStore();
const timezoneStore = useTimezoneStore();
const profileStore = PersonStores.useUserProfileStore();
const PEOPLE_TYPES = usePeopleTypesStore().PEOPLE_TYPES;
const useAuth = useAuthStore();

const canUpdate = ref({
  general: false,
  type: false,
});

const emit = defineEmits<{
  (e: EMIT.SAVE, value: GeneralProfileInterface): void;
}>();

interface GeneralPropsInterface {
  data?: GeneralProfileInterface;
  loading?: boolean;
  disableType?: boolean;
}

/**
 * handle props definition and assignment
 */
const props = defineProps<GeneralPropsInterface>();
const { data, loading, disableType } = toRefs(props);

/**
 * The general reference object to contain the form data.
 */
const general = ref({ ...data.value });

const rules = {
  first_name: { required, minLengthValue: minLength(2) },
  last_name: { required, minLengthValue: minLength(2) },
  email: {
    requiredIfCustom: requiredIf(() => {
      return !usePersonTypes.isStudent(profileStore.person?.people_type?.acronym as string);
    }),
    email,
  },
  person_type_id: { required },
  timezone_id: { required },
};

const v$ = useVuelidate(rules, general);

onBeforeMount(async () => {
  if (can([PERMISSIONS.PEOPLE_TYPES_INDEX]) && !peopleStore.peopleTypes.length) await peopleStore.getPeopleTypes();
  if (can([PERMISSIONS.TIMEZONES_INDEX]) && !timezoneStore.timezones.length) await timezoneStore.getTimezones();

  canUpdate.value.general = canUpdateGeneral();
  canUpdate.value.type = canUpdateType();
});

/**
 * Define the request update payload.
 */
const payload = computed(() => {
  const value = general.value;

  return {
    first_name: value.first_name,
    last_name: value.last_name,
    email: value.email,
    timezone_id: value.timezone_id,
    person_type_id: value.person_type_id,
  };
});

/**
 * Check if all component resources have completed loading.
 */
const isLoading = computed(() => {
  return loading.value || peopleStore.loading || timezoneStore.loading;
});

const onSave = () => {
  // Don't allow a submit event unless the fields are valid.
  if (v$.value.$invalid) return;

  emit(EMIT.SAVE, payload.value);
  useAuth.getAuthUserProfile();
  resetDirty();
};

const resetDirty = () => v$.value.$reset();

const clonedGeneralValue = () => {
  return { ...data.value };
};

const onCancel = () => {
  general.value = clonedGeneralValue();
  resetDirty();
};

const fieldStyles = (key: string, styles?: string): string => {
  const validation = v$.value[key].$invalid && v$.value[key].$dirty ? 'p-invalid' : '';
  return `${styles} ${validation}`;
};

const canUpdateGeneral = () => {
  const personType: string = profileStore.person?.people_type?.acronym as string;
  let permissionString = 'PEOPLE_UPDATE';
  let str: keyof typeof PERMISSIONS;

  switch (personType) {
    case PEOPLE_TYPES.SUPPORT_ADMIN: {
      permissionString += '_SUPPORT_ADMIN';
      break;
    }
    case PEOPLE_TYPES.SCHOOL_ADMIN: {
      permissionString += '_SCHOOL_ADMIN';
      break;
    }
    case PEOPLE_TYPES.SUPER_ADMIN: {
      permissionString += '_SUPER_ADMIN';
      break;
    }
  }

  str = permissionString as keyof typeof PERMISSIONS;

  return can([PERMISSIONS[str]]);
};

const canUpdateType = () => {
  const authUserType: string = useAuth.acronymType as string;
  const personType: string = profileStore.person?.people_type?.acronym as string;

  if (!canUpdate.value.general) return false;

  if (authUserType === PEOPLE_TYPES.SCHOOL_ADMIN && personType === PEOPLE_TYPES.SCHOOL_ADMIN) return false;

  if (authUserType === PEOPLE_TYPES.SUPPORT_ADMIN && personType === PEOPLE_TYPES.SUPPORT_ADMIN) return false;

  return true;
};
</script>

<template>
  <FormCard
    title="General"
    :dirty="v$.$anyDirty"
    :cancel="onCancel"
    :save="onSave"
    :save-disabled="v$.$anyDirty && v$.$invalid"
  >
    <template #form>
      <form class="w-full">
        <div class="formgrid grid">
          <div class="field col">
            <label for="firstname" :class="{ required: v$.$anyDirty }">First Name</label>
            <Skeleton v-if="isLoading" height="2.3rem" />
            <InputText
              v-else
              id="firstname"
              v-model="v$.first_name.$model"
              :class="fieldStyles('first_name', 'inputfield w-full')"
              :disabled="!canUpdate.general"
            />
          </div>

          <div class="field col">
            <label for="lastname" :class="{ required: v$.$anyDirty }">Last Name</label>
            <Skeleton v-if="isLoading" height="2.3rem" />
            <InputText
              v-else
              id="lastname"
              v-model="v$.last_name.$model"
              :class="fieldStyles('last_name', 'inputfield w-full')"
              :disabled="!canUpdate.general"
            />
          </div>
        </div>

        <div class="field">
          <label for="email">Email</label>
          <Skeleton v-if="isLoading" height="2.3rem" />
          <InputText
            v-else
            id="email"
            v-model="v$.email.$model"
            type="email"
            :class="fieldStyles('email', 'inputfield w-full')"
            :disabled="!canUpdate.general"
          />
        </div>

        <div class="field">
          <label for="type" :class="{ required: v$.$anyDirty }">Type</label>
          <Skeleton v-if="isLoading" height="2.3rem" />
          <Dropdown
            v-else
            id="type"
            v-model="v$.person_type_id.$model"
            :class="fieldStyles('person_type_id', 'w-full')"
            :options="peopleStore.peopleTypes"
            option-value="id"
            option-label="title"
            placeholder="Select a User Type"
            :disabled="!canUpdate.type || !!disableType"
            @change="v$.person_type_id.$touch"
          />
        </div>

        <div class="field">
          <label for="timezone" :class="{ required: v$.$anyDirty }">Timezone</label>
          <Skeleton v-if="isLoading" height="2.3rem" />
          <Dropdown
            v-else
            id="timezone"
            v-model="v$.timezone_id.$model"
            :class="fieldStyles('timezone_id', 'w-full')"
            :options="timezoneStore.timezones"
            option-value="id"
            option-label="name"
            placeholder="Select a Timezone"
            :disabled="!canUpdate.general"
          />
        </div>
      </form>
    </template>
  </FormCard>
</template>
